/**
 * Custom hook for contract ratification handling
 * Manages contract ratification state and special circumstances
 */

import { useCallback, useEffect, useState } from 'react';

import { ClauseProperties } from '@/utils/clauseIdUtils';
import { useClauseNotifications } from './useClauseNotifications';
import { useClauseState } from './useClauseState';

export interface RatificationConfig {
  allowAddendums: boolean;
  allowExtensions: boolean;
  allowRepairRequests: boolean;
  requireApprovalForSpecialCircumstances: boolean;
}

export const useContractRatification = (
  contractId: string,
  userRole: 'buyer' | 'seller' | 'agent',
  config: RatificationConfig = {
    allowAddendums: true,
    allowExtensions: true,
    allowRepairRequests: true,
    requireApprovalForSpecialCircumstances: false,
  }
) => {
  const [isRatified, setIsRatified] = useState(false);
  const [ratificationDate, setRatificationDate] = useState<Date | null>(null);
  const [specialCircumstances, setSpecialCircumstances] = useState<{
    addendums: ClauseProperties[];
    extensions: ClauseProperties[];
    repairRequests: ClauseProperties[];
  }>({
    addendums: [],
    extensions: [],
    repairRequests: [],
  });

  const {
    clauses,
    negotiationSummary,
    handleUpdateClauseStatus,
    getClausesByStatus,
  } = useClauseState({
    contractId,
    userRole,
    contractRatified: isRatified,
  });

  const {
    showContractStatusNotification,
    showSpecialCircumstanceNotification,
  } = useClauseNotifications(contractId, userRole, isRatified);

  // Check if contract is ratified
  useEffect(() => {
    const ratifiedClauses = getClausesByStatus('ratified');
    const hasRatifiedClauses = ratifiedClauses.length > 0;

    if (hasRatifiedClauses && !isRatified) {
      setIsRatified(true);
      setRatificationDate(new Date());
      showContractStatusNotification('ratified');
    }
  }, [
    clauses.length,
    getClausesByStatus,
    isRatified,
    showContractStatusNotification,
  ]);

  // Update special circumstances when clauses change
  useEffect(() => {
    const addendums = clauses.filter((clause) => clause.isAddendum);
    const extensions = clauses.filter((clause) => clause.isExtension);
    const repairRequests = clauses.filter((clause) => clause.isRepairRequest);

    setSpecialCircumstances({
      addendums,
      extensions,
      repairRequests,
    });
  }, [clauses.length]); // Only depend on length to avoid infinite re-renders

  // Ratify contract
  const ratifyContract = useCallback(
    async (reason?: string) => {
      try {
        // Update all clauses to ratified status
        const originalClauses = getClausesByStatus('original');
        const proposedClauses = getClausesByStatus('proposed');
        const counteredClauses = getClausesByStatus('countered');
        const acceptedClauses = getClausesByStatus('accepted');

        const allClausesToRatify = [
          ...originalClauses,
          ...proposedClauses,
          ...counteredClauses,
          ...acceptedClauses,
        ];

        // Update each clause to ratified status
        for (const clause of allClausesToRatify) {
          await handleUpdateClauseStatus(
            clause.clauseId,
            'ratified',
            reason || 'Contract ratified'
          );
        }

        setIsRatified(true);
        setRatificationDate(new Date());
        showContractStatusNotification('ratified');

        return true;
      } catch (error) {
        console.error('Error ratifying contract:', error);
        throw error;
      }
    },
    [
      getClausesByStatus,
      handleUpdateClauseStatus,
      showContractStatusNotification,
    ]
  );

  // Create addendum
  const createAddendum = useCallback(
    async (
      clauseId: string,
      addendumData: Partial<ClauseProperties>,
      reason?: string
    ) => {
      if (!config.allowAddendums) {
        throw new Error('Addendums are not allowed for this contract');
      }

      try {
        const addendumClause = await handleUpdateClauseStatus(
          clauseId,
          'proposed',
          reason || 'Addendum created'
        );

        // Mark as addendum
        if (addendumClause) {
          // Update clause properties to mark as addendum
          // This would typically be done through the API
          console.log('Creating addendum for clause:', clauseId);
        }

        showSpecialCircumstanceNotification('addendum', clauseId);
        return addendumClause;
      } catch (error) {
        console.error('Error creating addendum:', error);
        throw error;
      }
    },
    [
      config.allowAddendums,
      handleUpdateClauseStatus,
      showSpecialCircumstanceNotification,
    ]
  );

  // Request extension
  const requestExtension = useCallback(
    async (
      clauseId: string,
      extensionData: Partial<ClauseProperties>,
      reason?: string
    ) => {
      if (!config.allowExtensions) {
        throw new Error('Extensions are not allowed for this contract');
      }

      try {
        const extensionClause = await handleUpdateClauseStatus(
          clauseId,
          'proposed',
          reason || 'Extension requested'
        );

        // Mark as extension
        if (extensionClause) {
          console.log('Creating extension for clause:', clauseId);
        }

        showSpecialCircumstanceNotification('extension', clauseId);
        return extensionClause;
      } catch (error) {
        console.error('Error requesting extension:', error);
        throw error;
      }
    },
    [
      config.allowExtensions,
      handleUpdateClauseStatus,
      showSpecialCircumstanceNotification,
    ]
  );

  // Create repair request
  const createRepairRequest = useCallback(
    async (
      clauseId: string,
      repairData: Partial<ClauseProperties>,
      reason?: string
    ) => {
      if (!config.allowRepairRequests) {
        throw new Error('Repair requests are not allowed for this contract');
      }

      try {
        const repairClause = await handleUpdateClauseStatus(
          clauseId,
          'proposed',
          reason || 'Repair request created'
        );

        // Mark as repair request
        if (repairClause) {
          console.log('Creating repair request for clause:', clauseId);
        }

        showSpecialCircumstanceNotification('repair_request', clauseId);
        return repairClause;
      } catch (error) {
        console.error('Error creating repair request:', error);
        throw error;
      }
    },
    [
      config.allowRepairRequests,
      handleUpdateClauseStatus,
      showSpecialCircumstanceNotification,
    ]
  );

  // Check if special circumstances are allowed
  const canCreateAddendum = useCallback(
    (clauseId: string) => {
      return config.allowAddendums && isRatified;
    },
    [config.allowAddendums, isRatified]
  );

  const canRequestExtension = useCallback(
    (clauseId: string) => {
      return config.allowExtensions && isRatified;
    },
    [config.allowExtensions, isRatified]
  );

  const canCreateRepairRequest = useCallback(
    (clauseId: string) => {
      return config.allowRepairRequests && isRatified;
    },
    [config.allowRepairRequests, isRatified]
  );

  // Get available actions for a clause
  const getAvailableActions = useCallback(
    (clause: ClauseProperties) => {
      const actions = [];

      if (isRatified) {
        if (canCreateAddendum(clause.clauseId)) {
          actions.push('addendum');
        }
        if (canRequestExtension(clause.clauseId)) {
          actions.push('extension');
        }
        if (canCreateRepairRequest(clause.clauseId)) {
          actions.push('repair_request');
        }
      } else {
        // Regular negotiation actions
        if (clause.canCounter.includes(userRole)) {
          actions.push('counter');
        }
        if (clause.canAccept.includes(userRole)) {
          actions.push('accept');
        }
        if (clause.canReject.includes(userRole)) {
          actions.push('reject');
        }
        if (clause.canEdit.includes(userRole)) {
          actions.push('edit');
        }
      }

      return actions;
    },
    [
      isRatified,
      canCreateAddendum,
      canRequestExtension,
      canCreateRepairRequest,
      userRole,
    ]
  );

  return {
    // State
    isRatified,
    ratificationDate,
    specialCircumstances,
    negotiationSummary,

    // Actions
    ratifyContract,
    createAddendum,
    requestExtension,
    createRepairRequest,

    // Utilities
    canCreateAddendum,
    canRequestExtension,
    canCreateRepairRequest,
    getAvailableActions,
  };
};
